import { AccessToken, GetAccessTokenResponse } from '../models';
import { User } from '@us/web/shared/data-access';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { addSeconds, isAfter } from 'date-fns';
import { cloneDeep } from 'lodash';
import { AuthActions } from './auth.actions';

export const AUTH_FEATURE_KEY = 'auth';

export interface State {
    isLoading: boolean;
    token: AccessToken | null;
    apiDomain: string | null;
    user: User | null;
}

export const initialState: State = {
    token: null,
    isLoading: true,
    user: null,
    apiDomain: null
};

const onGetTokenSuccess = (state: State, data: GetAccessTokenResponse) => {
    const { access_token: accessToken, token_type: tokenType, expires_in: expiresIn, api_domain: apiDomain } = data;
    const expiresAt: Date = addSeconds(new Date(), Number(expiresIn));

    const token: AccessToken = {
        accessToken,
        tokenType,
        expiresIn,
        expiresAt: expiresAt.toJSON()
    };

    return { ...state, token, apiDomain, isLoading: false };
};

const onLoginSuccess = (state: State, user: User, tokenServer: GetAccessTokenResponse) => {
    const { access_token: accessToken, token_type: tokenType, expires_in: expiresIn } = tokenServer;
    const expiresAt: Date = addSeconds(new Date(), Number(expiresIn));

    const token: AccessToken = {
        accessToken,
        tokenType,
        expiresIn,
        expiresAt: expiresAt.toJSON()
    };

    return {
        ...state,
        token,
        user: user,
        isLoading: false,
        apiDomain: tokenServer.api_domain
    };
};

export const onLogout = () => cloneDeep(initialState);

export const authFeature = createFeature({
    name: AUTH_FEATURE_KEY,
    reducer: createReducer(
        initialState,
        on(AuthActions.getToken, (state: State): State => ({ ...state, isLoading: true })),
        on(AuthActions.getTokenSuccess, (state: State, { data }) => onGetTokenSuccess(state, data)),
        on(
            AuthActions.login,
            AuthActions.updateAccountProfile,
            (state: State): State => ({
                ...state,
                isLoading: true
            })
        ),
        on(AuthActions.loginSuccess, AuthActions.updateAccountProfileSuccess, (state: State, { user, token }) =>
            onLoginSuccess(state, user, token)
        ),
        on(
            AuthActions.loginFailure,
            AuthActions.updateAccountProfileFailure,
            (state: State): State => ({
                ...state,
                isLoading: false
            })
        ),
        on(AuthActions.logoutConfirmed, () => onLogout())
    )
});

export const { name, reducer, selectAuthState, selectIsLoading, selectToken, selectUser } = authFeature;

const isTokenValid = (token: AccessToken | null) =>
    !!token && !!token.accessToken && !!token.expiresAt && isAfter(new Date(token.expiresAt), new Date());

export const selectIsTokenValid = createSelector(selectAuthState, state => isTokenValid(state.token));

export const selectValidatedToken = createSelector(selectAuthState, state =>
    isTokenValid(state.token) ? state.token : null
);

export const selectApiDomain = createSelector(selectAuthState, state => state.apiDomain);

export const selectUserId = createSelector(selectUser, user => user?.id);
export const selectIsLoggedIn = createSelector(selectUser, user => user !== null);
export const selectIsNotLoggedIn = createSelector(selectUser, user => user === null);

export const selectUserPhoneNumber = createSelector(selectUser, user => user?.phone || null);
export const selectUserHasPassword = createSelector(selectUser, user => user?.pin_code_exist || false);
